import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CameraIcon, DefaultInfluencerIcon, TrashIcon } from '../../assets';
import { PERMISSIONS } from '../../config/constants';
import { ROUTES } from '../../config/routes/config';
import { CATEGORY_OPTIONS, SECTOR_OPTIONS } from '../../constants/brandOptions';
import { Confirm } from '../../context/confirm/confirm';
import {
  DELETE_BRAND,
  UPDATE_BRAND,
  UPDATE_BRAND_PROFILE_PICTURE,
} from '../../graphql/brands/mutation';
import { useNotification } from '../../hooks/useNotification';
import { usePermissions } from '../../hooks/usePermissions';
import type { SingleBrand } from '../../types';
import { BrandActivity } from '../brands/BrandActivity';
import { Tab } from '../campaigns/SingleCampaignTabs';
import { Button } from '../common/Button';
import FileUploaderInvisible from '../common/FileUploaderInvisible';
import { LateralProfileBrandMenu } from '../common/LateralProfileBrandMenu';
import { LateralProfileProspectMenu } from '../common/LateralProfileProspectMenu';
import { GenericDropdown } from '../inputs/GenericDropdown';
import { HoldedContactDropdown } from '../inputs/HoldedContactDropdown';
import { TextInput } from '../inputs/TextInput';

const SOURCE_OPTIONS = [
  'social_media',
  'website',
  'referral',
  'scouting',
  'event',
];

type BrandFormProps = {
  info?: SingleBrand;
  refetch: () => void;
  isProspect?: boolean;
};

export const BrandForm = ({ info, refetch, isProspect }: BrandFormProps) => {
  const { t } = useTranslation('common');
  const [brand, setBrand] = useState<any>(info);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openConvertConfirm, setOpenConvertConfirm] = useState(false);
  const navigate = useNavigate();
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const { isInfluentia, checkPermission } = usePermissions();

  const { notifySaveSuccess, notifySaveError } = useNotification();

  const [deleteBrandDB, { loading: loadingDelete }] = useMutation(DELETE_BRAND);

  const deleteBrand = () => {
    deleteBrandDB({
      variables: { brandId: brand.id },
      context: { headers: { 'x-apollo-operation-name': 'DeleteBrand' } },
      onCompleted: () => {
        notifySaveSuccess();
        navigate(isProspect ? ROUTES.PROSPECTS : ROUTES.BRANDS);
        refetch();
      },
      onError: () => notifySaveError(),
    });
  };

  const [updateBrand, { loading }] = useMutation(UPDATE_BRAND);
  const selectedSource = watch('source');

  const onSubmit = (data: any) => {
    updateBrand({
      variables: {
        brandId: brand.id,
        cif: data.cif,
        address: data.address,
        paymentTerms: data.paymentSteps,
        companyName: data.companyName,
        name: data.name,
        holdedIdSpain: data.holdedIdSpain,
        holdedIdAndorra: data.holdedIdAndorra,
        billingEmail: data.billingEmail,
        category: data.category,
        sector: data.sector,
        source: data.source,
        referralName:
          data.source === 'referral' ? data.referralName || '' : null,
      },
      onCompleted: () => {
        notifySaveSuccess();
        refetch();
      },
      onError: () => notifySaveError(),
    });
  };

  const [convertProspectToClient, { loading: loadingConvertProspect }] =
    useMutation(UPDATE_BRAND);

  const handleConvertProspectToClient = () => {
    convertProspectToClient({
      variables: {
        brandId: brand.id,
        isProspect: false,
      },
      onCompleted: (data) => {
        if (data?.updateBrand?.status !== 'success') {
          notifySaveError();
          return;
        }

        notifySaveSuccess();
        navigate(
          ROUTES.SINGLE_BRAND.replace(
            ':id',
            data?.updateBrand?.data || brand.id,
          ),
        );
      },
      onError: () => notifySaveError(),
    });
  };

  useEffect(() => {
    setBrand(info);
  }, [info]);

  useEffect(() => {
    if (brand) {
      setValue('name', brand.name);
      setValue('cif', brand.cif);
      setValue('address', brand.address);
      setValue('paymentSteps', brand.paymentTerms);
      setValue('companyName', brand.companyName);
      setValue('holdedIdSpain', brand.holdedIdSpain);
      setValue('holdedIdAndorra', brand.holdedIdAndorra);
      setValue('billingEmail', brand.billingEmail);
      setValue('category', brand.category || '');
      setValue('sector', brand.sector || '');
      setValue('source', brand.source || '');
      setValue('referralName', brand.referralName || '');
    }
  }, [brand]);

  const [updateProfilePicture] = useMutation(UPDATE_BRAND_PROFILE_PICTURE);

  const uploadFile = (file?: File) => {
    updateProfilePicture({
      variables: { file, brandId: brand.id },
      context: {
        headers: {
          'content-type': 'multipart/form-data',
          'x-apollo-operation-name': 'UpdateBrandProfilePicture',
        },
      },
      onCompleted: () => {
        notifySaveSuccess();
        refetch();
      },
      onError: () => notifySaveError(),
    });
  };

  const categoryOptions = CATEGORY_OPTIONS.map((val) => ({
    value: val,
    label: t(`enums.category.${val}`),
  }));

  const sectorOptions = SECTOR_OPTIONS.map((val) => ({
    value: val,
    label: t(`enums.sector.${val}`),
  }));

  const sourceOptions = SOURCE_OPTIONS.map((val) => ({
    value: val,
    label: t(`enums.source.${val}`),
  }));

  return (
    <div className="w-full flex flex-col gap-y-5">
      {brand && (
        <>
          {isInfluentia() && (
            <div className="lg:hidden flex flex-row gap-5">
              <div className="flex flex-row gap-x-2 overflow-auto customScroll pb-1 w-full">
                <Tab
                  to={(isProspect
                    ? ROUTES.SINGLE_PROSPECT
                    : ROUTES.SINGLE_BRAND
                  ).replace(':id', brand.id)}
                  id="personalInfoTab"
                  text={t('profile.menu.title1')}
                />
                <Tab
                  to={(isProspect
                    ? ROUTES.SINGLE_PROSPECT_MEMBERS
                    : ROUTES.SINGLE_BRAND_MEMBERS
                  ).replace(':id', brand.id)}
                  id="membersTab"
                  text={t('profile.menu.members')}
                />
                <Tab
                  to={(isProspect
                    ? ROUTES.SINGLE_PROSPECT_CAMPAIGNS
                    : ROUTES.SINGLE_BRAND_CAMPAIGNS
                  ).replace(':id', brand.id)}
                  id="campaignsTab"
                  text={t('profile.menu.title2')}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col lg:gap-x-5 lg:gap-y-0 gap-y-5">
            <Confirm
              open={openConfirm}
              onConfirm={() => deleteBrand()}
              id="deleteBrandDialog"
              setOpen={setOpenConfirm}
              type="delete"
              title={t('global.deleteTitle')}
              subtitle={t('global.deleteSubtitle')}
              confirmText={t('global.delete')}
              cancelText={t('global.cancel')}
            />
            <Confirm
              open={openConvertConfirm}
              onConfirm={() => handleConvertProspectToClient()}
              id="convertProspectDialog"
              setOpen={setOpenConvertConfirm}
              type="warning"
              title={t('prospects.convertToClientTitle')}
              subtitle={t('prospects.convertToClientSubtitle')}
              confirmText={t('prospects.convertToClient')}
              cancelText={t('global.cancel')}
            />
            <div className="flex flex-row lg:gap-12 h-full">
              {isInfluentia() &&
                (isProspect ? (
                  <LateralProfileProspectMenu brand={brand} />
                ) : (
                  <LateralProfileBrandMenu brand={brand} />
                ))}
              <div className="flex lg:flex-row flex-col w-full gap-12">
                {/* LEFT COLUMN - Form */}
                <div className="flex flex-col w-full lg:max-w-[500px]">
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-8"
                  >
                    {/* Profile Picture */}
                    <div className="flex flex-col">
                      <span className="text-[16px] uppercase mb-2.5">
                        {t('global.profilePicture')}
                      </span>
                      <div className="flex flex-row gap-5 items-center">
                        <div className="group relative size-[96px] items-center flex justify-center">
                          {!brand.profilePicture && (
                            <DefaultInfluencerIcon className="size-[96px] group-hover:opacity-50" />
                          )}
                          <CameraIcon className="absolute group-hover:block hidden z-[5]" />
                          {brand.profilePicture && (
                            <img
                              src={`${process.env.REACT_APP_AWS_PUBLIC_ENDPOINT}/${brand.profilePicture}`}
                              className="rounded-full size-[96px] object-cover group-hover:opacity-50"
                              alt=""
                            />
                          )}
                          <FileUploaderInvisible submitFile={uploadFile} />
                        </div>
                        <span className="text-2xl font-bold">{brand.name}</span>
                      </div>
                    </div>

                    {/* Información Del Negocio */}
                    <div className="flex flex-col">
                      <span className="text-[16px] uppercase mb-2">
                        {t('singleBrand.businessInfo')}
                      </span>
                      <div className="px-2.5 w-full">
                        <TextInput
                          type="text"
                          register={register}
                          registerName="name"
                          label={t('singleBrand.name')}
                          placeholder={t('singleBrand.name')}
                          selectedValue={brand.name}
                        />
                        {isInfluentia() && (
                          <>
                            <GenericDropdown
                              control={control}
                              name="category"
                              label={t('singleBrand.category')}
                              placeholder={t('singleBrand.category')}
                              options={categoryOptions}
                              isSearchable
                              isClearable={false}
                            />
                            <GenericDropdown
                              control={control}
                              name="sector"
                              label={t('singleBrand.sector')}
                              placeholder={t('singleBrand.sector')}
                              options={sectorOptions}
                              isSearchable
                              isClearable={false}
                            />
                            <GenericDropdown
                              control={control}
                              name="source"
                              label={t('singleBrand.source')}
                              placeholder={t('singleBrand.source')}
                              options={sourceOptions}
                              isClearable={false}
                            />
                            {selectedSource === 'referral' && (
                              <TextInput
                                type="text"
                                registerName="referralName"
                                register={register}
                                selectedValue={brand.referralName}
                                label={t('singleBrand.referralName')}
                                placeholder={t(
                                  'singleBrand.referralNamePlaceholder',
                                )}
                              />
                            )}
                          </>
                        )}
                        <TextInput
                          type="text"
                          register={register}
                          registerName="companyName"
                          label={t('brands.createRazon')}
                          placeholder={t('brands.createRazon')}
                          selectedValue={brand.companyName}
                        />
                        <TextInput
                          type="text"
                          register={register}
                          registerName="cif"
                          selectedValue={brand.cif}
                          label={t('singleBrand.CIFPlaceholder')}
                          placeholder={t('singleBrand.CIFPlaceholder')}
                        />
                        <TextInput
                          type="text"
                          registerName="address"
                          register={register}
                          maxLength={999}
                          selectedValue={brand.address}
                          label={t('singleBrand.address')}
                          placeholder={t('singleBrand.addressPlaceholder')}
                        />
                      </div>
                    </div>

                    {/* Pagos */}
                    <div className="flex flex-col">
                      <span className="text-[16px] uppercase mb-2">
                        {t('singleBrand.paymentDetails')}
                      </span>
                      <div className="px-2.5 w-full">
                        {isInfluentia() && (
                          <>
                            <HoldedContactDropdown
                              control={{ ...control }}
                              name="holdedIdSpain"
                              label={t('singleBrand.holdedIdSpain')}
                              placeholder={t('singleBrand.holdedIdSpain')}
                              error={errors.holdedIdSpain?.message as string}
                              type="spain"
                            />
                            <HoldedContactDropdown
                              control={{ ...control }}
                              name="holdedIdAndorra"
                              label={t('singleBrand.holdedIdAndorra')}
                              placeholder={t('singleBrand.holdedIdAndorra')}
                              error={errors.holdedIdAndorra?.message as string}
                              type="andorra"
                            />
                          </>
                        )}
                        <TextInput
                          type="text"
                          registerName="billingEmail"
                          register={register}
                          maxLength={999}
                          selectedValue={brand.billingEmail}
                          label={t('singleBrand.billingEmail')}
                          placeholder={t('singleBrand.billingEmail')}
                        />
                        <TextInput
                          type="number"
                          register={register}
                          registerName="paymentSteps"
                          label={t('singleBrand.payment')}
                          placeholder={t('singleBrand.paymentPlaceholder')}
                          errors={errors.paymentSteps}
                        />
                      </div>
                    </div>
                    <div className="px-2.5 flex justify-end gap-2">
                      <Button
                        text={t('global.save')}
                        loading={loading}
                        type="submit"
                        style="light-blue"
                        customClassnames="!px-14 flex text-center items-center justify-center"
                      />
                    </div>
                  </form>

                  <div
                    className={`w-full mt-5 ${!checkPermission(isProspect ? PERMISSIONS.DELETE_PROSPECT : PERMISSIONS.DELETE_BRAND) && '!hidden'}`}
                  >
                    <div className="flex items-end justify-end">
                      <Button
                        style="black"
                        id="deleteBrand"
                        customClassnames="flex w-full shrink-0 items-center justify-center !bg-red-status py-1.5 px-5 rounded-[4px]"
                        iconLeft={<TrashIcon />}
                        loading={loadingDelete}
                        onClick={() => setOpenConfirm(true)}
                        text={t('profile.deleteAccount')}
                      />
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN - Activity */}
                <div className="flex-1 min-w-0 flex flex-col gap-y-1 self-start lg:sticky lg:top-5 lg:h-[95vh]">
                  {isProspect &&
                    checkPermission(PERMISSIONS.UPDATE_PROSPECT) && (
                      <Button
                        text={t('prospects.convertToClient')}
                        loading={loadingConvertProspect}
                        onClick={() => setOpenConvertConfirm(true)}
                        style="dark-blue"
                        customClassnames="!px-6 flex text-center items-center justify-center ml-auto"
                      />
                    )}
                  <BrandActivity brandId={brand.id} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
